/** Styled components for OutputComparisonModal (split out of OutputComparisonModal.jsx, #1166). */
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Loader } from 'lucide-react';

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} 0.2s ease;
`;

export const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
  background: rgba(10, 10, 10, 0.95);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
`;

export const Title = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const VersionSelect = styled.select`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: #fff;
  padding: 8px 12px;
  font-size: 13px;
  cursor: pointer;
  min-width: 180px;
  &:focus { outline: none; border-color: rgba(99, 102, 241, 0.5); }
`;

export const VsLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #666;
`;

export const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #888;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
  &:hover { color: #fff; border-color: rgba(255, 255, 255, 0.2); }
`;

export const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const VarsRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: flex-end;
`;

export const ExampleControlsRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
`;

export const VarGroup = styled.div`
  flex: 1;
  min-width: 180px;
  max-width: 320px;
`;

export const VarLabel = styled.label`
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #f59e0b;
  margin-bottom: 4px;
  font-family: 'JetBrains Mono', 'SF Mono', monospace;
`;

export const VarInput = styled.textarea`
  width: 100%;
  min-height: 48px;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: #e0e0e0;
  font-size: 12px;
  font-family: 'JetBrains Mono', 'SF Mono', monospace;
  padding: 8px 10px;
  resize: vertical;
  &:focus { outline: none; border-color: rgba(245, 158, 11, 0.5); }
  &::placeholder { color: #444; }
`;

export const RunButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  background: ${p => p.disabled ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.8)'};
  color: ${p => p.disabled ? '#666' : '#fff'};
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.15s;
  flex-shrink: 0;
  align-self: flex-start;
  &:hover:not(:disabled) { background: rgba(34, 197, 94, 1); }
`;

export const Spinner = styled(Loader)`animation: ${spin} 1s linear infinite;`;

export const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  flex: 1;
  min-height: 0;
`;

export const ResultColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
`;

export const ColumnHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px 10px 0 0;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
`;

export const Badge = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: ${p => p.$bg || 'rgba(99, 102, 241, 0.15)'};
  color: ${p => p.$color || '#a5b4fc'};
`;

export const ResultBox = styled.div`
  flex: 1;
  min-height: 200px;
  max-height: 50vh;
  overflow-y: auto;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-top: none;
  border-radius: 0 0 10px 10px;
  padding: 16px;
  font-family: 'JetBrains Mono', 'SF Mono', monospace;
  font-size: 13px;
  line-height: 1.6;
  color: #ccc;
  white-space: pre-wrap;
  word-break: break-word;
`;

export const LoadingBox = styled(ResultBox)`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #555;
  gap: 8px;
`;

export const ErrorBox = styled(ResultBox)`
  color: #f87171;
  border-color: rgba(239, 68, 68, 0.2);
`;

export const EmptyBox = styled(ResultBox)`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #444;
  font-family: inherit;
`;

export const Accordion = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  overflow: hidden;
`;

export const DiffLauncherRow = styled.div`
  position: fixed;
  right: 24px;
  bottom: 24px;
  display: flex;
  gap: 8px;
  z-index: 1200;
`;

export const DiffLauncherBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid rgba(99, 102, 241, 0.35);
  background: rgba(20, 20, 40, 0.9);
  color: #a5b4fc;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: rgba(30, 30, 60, 0.95); }
`;

export const DiffPanel = styled.div`
  position: fixed;
  left: 24px;
  right: 24px;
  bottom: 24px;
  max-height: 62vh;
  z-index: 1250;
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 12px;
  background: rgba(10, 10, 20, 0.97);
  overflow: hidden;
  box-shadow: 0 10px 32px rgba(0, 0, 0, 0.5);
`;

export const DiffPanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  color: #c7d2fe;
  font-size: 13px;
  font-weight: 700;
`;

export const AccordionToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.03);
  border: none;
  color: #888;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  transition: color 0.15s;
  &:hover { color: #aaa; }
`;

export const AccordionBody = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

export const DiffBlock = styled.div`
  padding: 8px 12px;
  font-family: 'JetBrains Mono', 'SF Mono', monospace;
  font-size: 11px;
  line-height: 1.6;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

export const DiffLine = styled.div`
  padding: 1px 4px;
  border-radius: 2px;
  background: ${p =>
    p.$type === 'added' ? 'rgba(34, 197, 94, 0.12)' :
    p.$type === 'removed' ? 'rgba(239, 68, 68, 0.12)' : 'transparent'};
  color: ${p =>
    p.$type === 'added' ? '#4ade80' :
    p.$type === 'removed' ? '#f87171' : '#888'};
`;

export const FieldLabel = styled.div`
  padding: 6px 12px;
  font-size: 11px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
`;

export const EditToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid ${p => p.$active ? 'rgba(245, 158, 11, 0.4)' : 'rgba(255, 255, 255, 0.12)'};
  background: ${p => p.$active ? 'rgba(245, 158, 11, 0.15)' : 'rgba(255, 255, 255, 0.04)'};
  color: ${p => p.$active ? '#f59e0b' : '#888'};
  &:hover {
    border-color: ${p => p.$active ? 'rgba(245, 158, 11, 0.6)' : 'rgba(255, 255, 255, 0.25)'};
    color: ${p => p.$active ? '#fbbf24' : '#aaa'};
  }
`;

export const EditPanesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`;

export const EditPane = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const EditLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const EditTextarea = styled.textarea`
  width: 100%;
  min-height: ${p => p.$minHeight || '80px'};
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(245, 158, 11, 0.2);
  border-radius: 8px;
  color: #e0e0e0;
  font-family: 'JetBrains Mono', 'SF Mono', Menlo, monospace;
  font-size: 12px;
  line-height: 1.5;
  padding: 10px 12px;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: rgba(245, 158, 11, 0.5);
    box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.08);
  }
`;

export const SaveVersionBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${p => p.$success ? 'rgba(34, 197, 94, 0.2)' : p.disabled ? 'rgba(99, 102, 241, 0.15)' : 'rgba(99, 102, 241, 0.7)'};
  color: ${p => p.$success ? '#4ade80' : p.disabled ? '#555' : '#fff'};
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.15s;
  &:hover:not(:disabled) { background: rgba(99, 102, 241, 0.9); }
`;

export const SmallSelect = styled.select`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: #fff;
  padding: 8px 10px;
  font-size: 12px;
  min-width: 220px;
  max-width: 100%;
`;

export const SmallButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: #ddd;
  font-size: 12px;
  font-weight: 600;
  cursor: ${p => (p.disabled ? 'not-allowed' : 'pointer')};
`;

export const MetaInfo = styled.div`
  font-size: 11px;
  color: #94a3b8;
  background: rgba(148, 163, 184, 0.08);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 8px;
  padding: 8px 10px;
`;

export const PolishedResultBox = styled(ResultBox)`
  border-color: rgba(99, 102, 241, 0.25);
  border-top: 1px solid rgba(99, 102, 241, 0.25);
`;

export const PolishedLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  font-size: 11px;
  font-weight: 600;
  color: #a5b4fc;
  background: rgba(99, 102, 241, 0.06);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-bottom: none;
  border-radius: 8px 8px 0 0;
`;
